const yup = require("yup");

const MAX_PAYMENTS = 999;
const MAX_AMOUNT = 1_000_000;
const CENTS = /^\d+(\.\d{1,2})?$/;

const paymentLink = yup.object().shape({
  name: yup.string().trim().required("A name is required"),
  description: yup.string().nullable(),
  linkType: yup.string(),
  interval: yup.string(),
  amountMode: yup.string().required(),
  amount: yup.number().when(["amountMode", "linkType"], {
    is: (amountMode, linkType) =>
      amountMode === "FIXED" || linkType === "SUBSCRIPTION",
    then: (schema) =>
      schema
        .typeError("An amount is required")
        .moreThan(0, "The amount must be greater than zero")
        .max(MAX_AMOUNT, `The amount cannot exceed ${MAX_AMOUNT}`)
        .test("two-decimals", "Use at most two decimal places", isCents)
        .required("An amount is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  minAmount: yup
    .number()
    .nullable()
    .transform(emptyToNull)
    .min(0, "The minimum cannot be negative")
    .max(MAX_AMOUNT, `The minimum cannot exceed ${MAX_AMOUNT}`)
    .test("two-decimals", "Use at most two decimal places", isCents),
  maxAmount: yup
    .number()
    .nullable()
    .transform(emptyToNull)
    .moreThan(0, "The maximum must be greater than zero")
    .max(MAX_AMOUNT, `The maximum cannot exceed ${MAX_AMOUNT}`)
    .test("two-decimals", "Use at most two decimal places", isCents)
    .test(
      "above-minimum",
      "The maximum must be at least the minimum",
      (value, context) =>
        value == null ||
        context.parent.minAmount == null ||
        value >= context.parent.minAmount,
    ),
  maxPayments: yup
    .number()
    .nullable()
    .transform(emptyToNull)
    .integer("Use a whole number")
    .min(1, "A term must be at least 1 payment")
    .max(MAX_PAYMENTS, `A term cannot exceed ${MAX_PAYMENTS} payments`),
  cashDiscountEnabled: yup.boolean(),
  presetAmountsText: yup.string().when("amountMode", {
    is: "PAYER_CHOOSES",
    then: (schema) =>
      schema.test(
        "valid-presets",
        "Enter up to four amounts greater than zero",
        isPresetAmounts,
      ),
    otherwise: (schema) => schema.nullable(),
  }),
  tipEnabled: yup.boolean(),
  tipPresetPercentages: yup.string().when("tipEnabled", {
    is: true,
    then: (schema) =>
      schema.test(
        "valid-percentages",
        "Enter up to four percentages between 0 and 100",
        isTipPresets,
      ),
    otherwise: (schema) => schema.nullable(),
  }),
  expiresAt: yup
    .string()
    .nullable()
    .test(
      "not-past",
      "Choose a date in the future",
      (value) => !value || new Date(value).getTime() > Date.now(),
    ),
  endsAt: yup
    .string()
    .nullable()
    .test(
      "not-past",
      "Choose a date in the future",
      (value) => !value || new Date(value).getTime() > Date.now(),
    ),
  successType: yup.string().required(),
  successRedirectUrl: yup.string().when("successType", {
    is: "REDIRECT",
    then: (schema) =>
      schema
        .trim()
        .url("Enter a valid URL")
        .required("A destination URL is required"),
    otherwise: (schema) => schema.nullable(),
  }),
});

function isPresetAmounts(value) {
  if (!value || !String(value).trim()) return true;

  const parts = String(value)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length || parts.length > 4) return false;

  return parts.every((part) => {
    const amount = Number(part);
    return Number.isFinite(amount) && amount > 0;
  });
}

function isTipPresets(value) {
  if (!value || !String(value).trim()) return true;

  const parts = String(value)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length || parts.length > 4) return false;

  return parts.every((part) => {
    const percentage = Number(part);
    return Number.isFinite(percentage) && percentage > 0 && percentage <= 100;
  });
}

function isCents(value) {
  return value == null || CENTS.test(String(value));
}

function emptyToNull(value, original) {
  return original === "" || original === undefined ? null : value;
}

export default paymentLink;
